import React, { useState, useEffect } from "react";
import {
  initialNodeStyle,
  initialDirectedWeightedEdgeStyle,
  visitedDirectedWeightedEdgeStyle,
} from "./FlowGraphStyle";
import { min } from "d3";
const FoldFulkerson = ({
  cytoscape,
  adjacencyList,
  numNodes,
  edgeWeights,
  sourceNode,
  sinkNode,
}) => {
  const [totalFlow, setTotalFlow] = useState(0);
  const [originalEdgeWeights, setOriginalEdgeWeights] = useState({});
  const [edgeWeights1, setEdgeWeights] = useState({ ...edgeWeights });
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [selectedPathFlows, setSelectedPathFlows] = useState([]);
  const [currentAction, setCurrentAction] = useState("Find Augmenting Path");
  const [isAugmentingPathFound, setIsAugmentingPathFound] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  //let newParent = {}; // Initialize parent object
  const [newParent, setNewParent] = useState({});
  // let updatedParent = null; // Initialize updatedParent to null
  const [updatedParent, setUpdatedParent] = useState(null);
  const [showPathInput, setShowPathInput] = useState(false);

  const handleTogglePathInput = () => {
    cytoscape.elements("edge").style(initialDirectedWeightedEdgeStyle);
    setShowPathInput((prev) => !prev);
  };
  const handlePathSubmit = (event) => {
    cytoscape.elements("edge").style(initialDirectedWeightedEdgeStyle);
    event.preventDefault();
    setShowPathInput(false);
    const newPath = event.target.elements.path.value.split("-");

    // Validate the path
    if (isValidPath(newPath)) {
      let tempNewParent = { ...newParent };
      let isValid = true; // Flag to check if the path is valid

      // Iterate over the path nodes
      for (let i = 1; i < newPath.length; i++) {
        const currentNode = newPath[i];
        const previousNode = newPath[i - 1];

        // Check if the edge weight is greater than 0
        //console.log(edgeWeights1[`${previousNode}-${currentNode}`]);
        if (
          edgeWeights1[`${previousNode}-${currentNode}`] <= 0 ||
          edgeWeights1[`${previousNode}-${currentNode}`] === undefined
        ) {
          isValid = false;
          break; // Exit the loop if the edge weight is not greater than 0
        }

        // Update the parent object
        tempNewParent[currentNode] = previousNode;
      }

      if (isValid) {
        // If the path is valid, update the newParent state and call augmentingPath
        setNewParent(tempNewParent);
        const flow = augmentingPath(true, tempNewParent);
        let newTotalFlow = totalFlow + flow;
        setTotalFlow(newTotalFlow);
        const updatedSelectedPaths = [...selectedPaths, newPath];
        const updatedSelectedPathFlows = [...selectedPathFlows, flow];
        setSelectedPaths(updatedSelectedPaths);
        setSelectedPathFlows(updatedSelectedPathFlows);
      } else {
        alert("Invalid path! Please enter a valid path.");
      }
    } else {
      alert("Invalid path! Please enter a valid path.");
    }
  };

  const isValidPath = (path, numNodes) => {
    if (
      path.length < 2 ||
      path[0] !== sourceNode ||
      path[path.length - 1] !== sinkNode
    ) {
      return false; // If the path is not from source to sink, return false
    }

    // Check if the nodes between source and sink are within the range from 1 to numNodes
    for (let i = 1; i < path.length - 1; i++) {
      const node = parseInt(path[i]);
      if (isNaN(node) || node < 1 || node > numNodes) {
        return false; // If any node is not within the range, return false
      }
    }

    return true; // If all conditions are satisfied, return true
  };

  useEffect(() => {
    setOriginalEdgeWeights({ ...edgeWeights });
  }, []);

  const augmentingPath = (found, newParent) => {
    if (found) {
      let current = sinkNode;
      let minFlow = Infinity;

      // Find the minimum flow along the augmenting path
      while (current !== sourceNode) {
        let prev = newParent[current];
        let edgeId = `${prev}-${current}`;
        minFlow = Math.min(minFlow, edgeWeights1[edgeId]);

        current = prev;
      }

      // Update the edge weights along the augmenting path
      current = sinkNode;
      while (current !== sourceNode) {
        let prev = newParent[current];
        let edgeId = `${prev}-${current}`;
        let backEdgeId = `${current}-${prev}`;
        edgeWeights1[edgeId] -= minFlow;
        edgeWeights1[backEdgeId] = minFlow;

        let sourceId = prev === "S" ? prev : `node${prev}`;
        let targetId = current === "T" ? current : `node${current}`;

        let selectedEdges = cytoscape.edges(
          `[source='${sourceId}'][target='${targetId}']`
        );
        selectedEdges.data("weight", edgeWeights1[edgeId]);
        selectedEdges.style(visitedDirectedWeightedEdgeStyle);
        let selectedBackEdge = cytoscape.edges(
          `[source='${targetId}'][target='${sourceId}']`
        );
        selectedBackEdge.data("weight", edgeWeights1[backEdgeId]);
        selectedBackEdge.style(visitedDirectedWeightedEdgeStyle);

        current = prev;
      }
      setSelectedPathFlows((prevFlows) => [...prevFlows, minFlow]);

      return minFlow;
    }

    return 0;
  };

  const resetAnimation = () => {
    for (const edgeId in originalEdgeWeights) {
      const [source, target] = edgeId.split("-");
      let sourceId = source === "S" ? source : `node${source}`;
      let targetId = target === "T" ? target : `node${target}`;
      const selectedEdges = cytoscape.edges(
        `[source='${sourceId}'][target='${targetId}']`
      );
      const backEdges = cytoscape.edges(
        `[source='${targetId}'][target='${sourceId}']`
      );

      if (selectedEdges.nonempty()) {
        selectedEdges.data("weight", originalEdgeWeights[edgeId]);
        backEdges.data("weight", 0);
        backEdges.style(initialDirectedWeightedEdgeStyle);
        selectedEdges.style(initialDirectedWeightedEdgeStyle);
      }
    }
    cytoscape.elements("node").style(initialNodeStyle);

    setTotalFlow(0);
    setNewParent({});
    setUpdatedParent(null);

    setSelectedPaths([]);
    setSelectedPathFlows([]);
    setCurrentAction("Find Augmenting Path");
    setEdgeWeights({ ...originalEdgeWeights });
  };

  return (
    <div className="topological-sort-container">
      <div className="topological-sort-buttons" style={{ marginTop: "4%" }}>
        <button
          className="animate-reset-button"
          onClick={handleTogglePathInput}
          disabled={isAnimating}
        >
          Enter Path
        </button>
        {showPathInput && (
          <form onSubmit={handlePathSubmit}>
            <label htmlFor="path">Enter a new path (e.g., S-3-4-T): </label>
            &nbsp;&nbsp;
            <input type="text" id="path" name="path" required />
            <br></br>
            <button
              className="animate-reset-button"
              type="submit"
              disabled={isAnimating}
              style={{ marginTop: "2%" }}
            >
              Submit
            </button>
            &nbsp;&nbsp; &nbsp;&nbsp;
            <button
              className="animate-reset-button"
              onClick={resetAnimation}
              disabled={isAnimating}
              style={{ marginTop: "2%" }}
            >
              Start Over
            </button>
          </form>
        )}
      </div>
      <div className="queue-title" style={{ marginTop: "2%" }}>
        Total Flow: {totalFlow}
      </div>
      <div className="table-container" style={{ marginTop: "2%" }}>
        <table className="custom-table">
          <thead>
            <tr>
              <th>Selected Path</th>
              <th>Minimum Flow</th>
            </tr>
          </thead>
          <tbody>
            {selectedPaths.map((path, index) => (
              <tr key={index}>
                <td>
                  Path {index + 1}: {path.join(" -> ")}
                </td>
                <td>{selectedPathFlows[index]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FoldFulkerson;
